import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { State, StatusCategory } from '../../../shared/models/generated/ziroModels';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { Observable, Subscription, tap, timer } from 'rxjs';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { SystemStatusContext } from '../../../shared/contexts/system-status.context';
import { ProxyKeysContext } from '../../contexts/proxy-keys.context';
import { DateTimeService } from '../../../shared/services/date-time.service';
import { delayWhen, map } from 'rxjs/operators';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

interface GenerateKeyFormData {
  secret: string;
}

@Component({
  selector: 'ziro-generate-key',
  templateUrl: './generate-key.component.html',
  styleUrls: ['../../admin-page.scss'],
  standalone: false,
})
export class GenerateKeyComponent extends ZiroFormAbstractDirective<GenerateKeyFormData> implements OnInit, OnDestroy {
  protected _ziroFormStateService: ZiroFormStateService;
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _systemStatusContext = inject(SystemStatusContext);
  private _proxyKeysContext = inject(ProxyKeysContext);
  private _dateTimeService = inject(DateTimeService);

  ButtonStyles = ButtonStyles;
  isLoading = true;
  ziroIcons = ZiroIcons;
  proxyKey: string;
  healthStatusAlertCss: 'alert-warning' | 'alert-info' | 'alert-success';
  healthStatusAlertText: string;

  formConfig = {
    fields: {
      secret: {
        label: 'tkey;admin.proxy_server_management.generate_key.secret.label',
        helpText: 'tkey;admin.proxy_server_management.generate_key.secret.helptext',
        dataAutomation: 'generate-key-secret',
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.TEXT,
          isWhiteWhileDisabled: true,
          disablePointerEvents: true,
        }),
        disabled: () => true,
        disabledTooltip: () => {
          if (!this.entity?.secret) {
            return {
              content: 'tkey;admin.proxy_server_management.generate_key.secret.disabled.tooltip',
              params: {},
            };
          }
        },
      },
    },
  };

  private _subs = new Subscription();

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;admin.proxy_server_management.title' },
      { label: 'tkey;admin.proxy_server_management.generate_key.title' },
    ]);

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() =>
      this._bottomNavService.setBottomNavValidationError(!this.isFormValid()),
    );
    this._subs.add(formSubmittedSub);

    const healthStatusSub = this._systemStatusContext.state$.subscribe((healthStatus) => {
      const proxyServerHealthStatus = healthStatus.healthStatuses.find(
        (status) => status.category === StatusCategory.PROXY_SERVER,
      );
      if (!proxyServerHealthStatus) {
        this.healthStatusAlertCss = undefined;
        this.healthStatusAlertText = undefined;
      } else if (proxyServerHealthStatus.state === State.OK) {
        this.healthStatusAlertCss = 'alert-success';
        this.healthStatusAlertText = 'tkey;admin.proxy_server_management.generate_key.health_status_alert.success';
      } else if (proxyServerHealthStatus.state === State.WARNING) {
        if (this.proxyKey) {
          this.healthStatusAlertCss = 'alert-info';
          this.healthStatusAlertText = 'tkey;admin.proxy_server_management.generate_key.health_status_alert.info';
        } else {
          this.healthStatusAlertCss = 'alert-warning';
          this.healthStatusAlertText = 'tkey;admin.proxy_server_management.generate_key.health_status_alert.warning';
        }
      }
    });
    this._subs.add(healthStatusSub);

    const proxyKeySub = this._proxyKeysContext.state$.subscribe((settings) => {
      if (!this.proxyKey) {
        this._setEntity(settings.keyFirstThree ? `${settings.keyFirstThree}•••••••••••••••••••••••••••••••••` : '');
      }
      this._initBottomNav(!!settings.keyFirstThree);
      this._setBottomNavTimestamp(settings.generatedDate);
      this.isLoading = false;
    });

    this._subs.add(proxyKeySub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.unsubscribe();
  }

  onCopyUriClick(t: NgbTooltip): void {
    timer(1000).subscribe(() => {
      t.close();
    });
  }

  protected submit(): Observable<void> {
    return this.entity?.secret ? this._regenerateSecret() : this._generateSecret();
  }

  private _generateSecret(): Observable<void> {
    return this._proxyKeysContext.post().pipe(
      map((response) => {
        this.proxyKey = response.proxyKey;
        this._setEntity(response.proxyKey);
        this._allowPointerEvents();
      }),
      delayWhen(() => this._systemStatusContext.getSystemStatus()),
      tap(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.KEY,
          'tkey;admin.proxy_server_management.generate_key.success_toast.title',
          'tkey;admin.proxy_server_management.generate_key.success_toast.message',
        );
      }),
    );
  }

  private _allowPointerEvents(): void {
    this.fieldChannels.secret.componentConfigSource.next(
      new ZiroTextConfig({
        htmlInputType: HtmlInputType.TEXT,
        isWhiteWhileDisabled: true,
        disablePointerEvents: false,
      }),
    );
  }

  private _initBottomNav(isExisting: boolean): void {
    const buttons: BottomNavButton[] = [];
    if (isExisting) {
      buttons.push({
        id: 'proxy-server-management-delete-key-button',
        label: 'tkey;admin.proxy_server_management.generate_key.delete_button.label',
        buttonClass: ButtonStyles.DANGER,
        icon: ZiroIcons.DELETE_SMALL,
        dataAutomation: 'proxy-server-management-delete-key-button',
        cb: () => this._onDeleteClick(),
      });
    }
    buttons.push({
      id: 'proxy-server-management-generate-key-button',
      label: isExisting
        ? 'tkey;admin.proxy_server_management.generate_key.regenerate_button.label'
        : 'tkey;admin.proxy_server_management.generate_key.generate_button.label',
      buttonClass: ButtonStyles.PRIMARY,
      icon: ZiroIcons.KEY,
      submitSubject: this._validateAndSubmitSource,
      dataAutomation: 'proxy-server-management-generate-key-button',
      type: ButtonTypes.SUBMIT,
    });
    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(buttons));
  }

  private _setBottomNavTimestamp(generatedAtDate: string): void {
    let helpText: string;
    if (generatedAtDate) {
      helpText = `
              <i class="${ZiroIcons.TIMESTAMP}"></i>
                ${this._translateService.instant('tkey;admin.proxy_server_management.generate_key.generated_date', {
                  date: this._dateTimeService.formatDate(generatedAtDate),
                })}
              `;
    } else {
      helpText = '';
    }

    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        helpText: helpText,
        hasValidationError: false,
      }),
    );
  }

  private _onDeleteClick(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center text-danger',
        title: this._translateService.instant('tkey;admin.proxy_server_management.generate_key.delete_button.label'),
        promptBody: this._translateService.instant('tkey;admin.proxy_server_management.generate_key.delete_modal.body'),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._deleteSettings(),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteSettings(): Observable<void> {
    return this._proxyKeysContext.delete().pipe(
      delayWhen(() => this._systemStatusContext.getSystemStatus()),
      map(() => {
        this.entitySource.next({ secret: null });
        this.proxyKey = null;
        this._toastService.push(
          ToastTypes.INFO,
          `${ZiroIcons.DELETE_SMALL} text-danger`,
          'tkey;admin.proxy_server_management.generate_key.delete_toast.title',
          'tkey;admin.proxy_server_management.generate_key.delete_toast.message',
        );
        this._changeDetectorRef.detectChanges();
      }),
    );
  }

  private _regenerateSecret(): Observable<void> {
    const options = {
      windowClass: 'regenerate-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.REFRESH,
        iconClass: 'text-primary',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center',
        title: this._translateService.instant(
          'tkey;admin.proxy_server_management.generate_key.regenerate_button.label',
        ),
        promptBody: this._translateService.instant(
          'tkey;admin.proxy_server_management.generate_key.regenerate_modal.body',
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;admin.proxy_server_management.generate_key.regenerate_button.label',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._regenerateSettings(),
          },
        ],
      },
    };

    return this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _regenerateSettings(): Observable<void> {
    return this._proxyKeysContext.delete().pipe(delayWhen(() => this._generateSecret()));
  }

  private _setEntity(key: string): void {
    this.entitySource.next({ secret: key ?? '' });
  }
}
